import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { QRCode } from "@progress/kendo-react-barcodes";
import {
	PaymentSlipDataInput,
	formatPaymentSlipData,
} from "./qr-payment-helpers";
import { convertToUtf8 } from "../../utils";

interface QrCodeValidatorProps {
	paymentSlipData: PaymentSlipDataInput;
	onValidationComplete: (result: QrCodeValidationResult) => void;
}

export interface QrCodeValidationResult {
	success: boolean;
	error?: string;
	qrData?: string;
	renderError?: string;
}

export const QrCodeValidator: React.FC<QrCodeValidatorProps> = ({
	paymentSlipData,
	onValidationComplete,
}) => {
	const [hasValidated, setHasValidated] = useState(false);

	const {
		data: formattedPaymentSlipData,
		qrReceipt: formattedQrReceipt,
		error: formatError,
	} = formatPaymentSlipData(paymentSlipData);

	useEffect(() => {
		if (hasValidated) return;

		if (!formattedPaymentSlipData || !formattedQrReceipt) {
			setHasValidated(true);
			onValidationComplete({
				success: false,
				error: formatError,
				qrData: formattedQrReceipt || undefined,
			});
			return;
		}

		const timer = setTimeout(() => {
			setHasValidated(true);
			onValidationComplete({
				success: true,
				qrData: formattedQrReceipt,
			});
		}, 100);

		return () => clearTimeout(timer);
	}, [hasValidated, formattedPaymentSlipData, formattedQrReceipt, formatError, onValidationComplete]);

	const handleRenderError = (error: Error) => {
		if (!hasValidated) {
			setHasValidated(true);
			onValidationComplete({
				success: false,
				renderError: error.message,
				qrData: formattedQrReceipt || undefined,
			});
		}
	};

	if (!formattedPaymentSlipData || !formattedQrReceipt) {
		return null;
	}

	const qrReceiptUtf8Bytes = convertToUtf8(formattedQrReceipt);

	return (
		<ErrorBoundary
			fallback={<div />}
			onError={handleRenderError}
		>
			<div
				style={{
					position: "absolute",
					left: "-9999px",
					top: "-9999px",
					visibility: "hidden",
				}}
			>
				<QRCode
					value={qrReceiptUtf8Bytes}
					errorCorrection="M"
					encoding="ISO_8859_1"
					overlay={{
						type: "swiss",
					}}
					style={{
						width: "46mm",
						height: "46mm",
					}}
				/>
			</div>
		</ErrorBoundary>
	);
};
